import { ChangeEmitter } from '@/application/ChangeEmitter';
import {
    createCuttingShape,
    invalidateCuttingShape,
    loadCuttingShape,
    primeCuttingShape,
} from '@/application/cuttingShape/loadCuttingShape';
import type { ICuttingShapeEngine } from '@/application/ports/ICuttingShapeEngine';
import type { ICuttingShapeRepository } from '@/application/ports/ICuttingShapeRepository';
import type { IProductImageRepository } from '@/application/ports/IProductImageRepository';
import type { IProductStateRepository } from '@/application/ports/IProductStateRepository';
import type { CuttingShape } from '@/domain/cuttingShape/CuttingShape';
import type { TUserContext } from '@/application/selection/TUserContext';
import type { TSide } from '@/domain/shared/TSide';

// The cutting-path controller. The shape lives in the shared cuttingShapeRepository
// (source of truth for "does this product have a cut shape") and NEVER in the entity
// repository — it is product state, not template state, so it must not be serialised with
// the template. It is also not selectable, so it needs no selection interop.
//
// One controller instance per side. The shape itself is side-agnostic; the side only
// decides whether this instance paints it mirrored.
//
// That split is why setCutting and clear touch state ONLY. A shape change is app-wide but
// every mounted side has its own engine to repaint, and a per-side controller can only
// reach its own. Callers go through useCuttingShapeActions, which mutates once and then
// fans paintFromState out across the sides — otherwise the face the user isn't looking at
// keeps a stale overlay.
class CuttingShapeController {
    constructor(
        private readonly _engine: ICuttingShapeEngine,
        private readonly _repository: ICuttingShapeRepository,
        private readonly _productStateRepository: IProductStateRepository,
        private readonly _productImageRepository: IProductImageRepository,
        private readonly _side: TSide,
        private readonly _changeEmitter: ChangeEmitter,
    ) {}

    setCutting(file: File, uuid: string): void {
        const shape = createCuttingShape(file, uuid);
        this._repository.set(shape);
        primeCuttingShape(shape);
        this._productStateRepository.patchProduct({ cuttingPath: uuid });
        this._changeEmitter.emit();
    }

    clear(): void {
        const product = this._productStateRepository.getProduct();
        if (product?.cuttingPath) {
            invalidateCuttingShape(product.cuttingPath);
        }
        this._repository.set(undefined);
        this._productStateRepository.patchProduct({ cuttingPath: null });
        this._changeEmitter.emit();
    }

    // Paint whatever the repo currently holds onto this side's engine, or wipe the side if
    // the product has no shape any more. No fetching and no state write — this is the view
    // half of a state change that has already happened.
    paintFromState(userCtx: TUserContext): Promise<void> {
        const shape = this._repository.get();
        if (!shape) {
            this._engine.clear();
            return Promise.resolve();
        }
        return this._paint(shape, !(userCtx.isAdmin && userCtx.isProductEdition));
    }

    // A — populate the shape repo from the current product's state and paint it onto this
    // side's engine. Repo hydration is idempotent (the other side's mount ran first → the
    // repo already holds the shape), but painting still runs so each per-side engine gets
    // its own draw.
    async syncFromState(userCtx: TUserContext): Promise<void> {
        const product = this._productStateRepository.getProduct();
        if (!product || product.has3d || !product.cuttingPath) {
            return;
        }

        // Both sides call this in parallel at mount. The shape is memoised by uuid so the
        // first call triggers the fetch and the second awaits the same Promise → a single
        // blob URL shared between sides.
        const shape = await loadCuttingShape(this._productImageRepository, product.cuttingPath);
        if (!shape) {
            return;
        }
        this._repository.set(shape);

        // An admin setting up the product sees the outline over an unmasked canvas;
        // everyone else gets the preview, with whatever falls outside the shape masked away.
        await this._paint(shape, !(userCtx.isAdmin && userCtx.isProductEdition));
        this._changeEmitter.emit();
    }

    private _paint(shape: CuttingShape, maskOutsideCut: boolean): Promise<void> {
        return this._engine.paint(shape, { maskOutsideCut, mirrored: this._side === 'verso' });
    }
}

export { CuttingShapeController };
